<template>
    <div class="ngdialog-content">
        <dialog-header>
            <h4 v-if="isSystem">{{ $t('organizations.add_system') }}</h4>
            <h4 v-else>{{ $t('organizations.invite_person') }}</h4>
        </dialog-header>

        <form name="inviteForm" novalidate @submit.prevent="submitHandler" class="form">
            <div class="ngdialog__body">
                <fieldset class="form__group">
                    <div class="row form__row" v-if="!isSystem">
                        <label class="form__label">
                            <input
                                ref="email"
                                type="email"
                                class="form__field"
                                name="email"
                                v-model.trim="$v.formData.email.$model"
                                :placeholder="$t('fields.email')"
                                required
                                autofocus
                                @focus="hasFocus = true"
                                @blur="hasFocus = false"
                                :class="{
                                    'is-invalid': $v.formData.email.$invalid,
                                    'is-dirty': $v.formData.email.$dirty,
                                }"
                            />

                            <div
                                class="form__tooltip"
                                v-if="hasFocus && $v.formData.email.$anyError"
                            >
                                <span v-if="!$v.formData.email.required">
                                    {{ $t('fields.email_e_required') }}
                                </span>

                                <span v-if="!$v.formData.email.email">
                                    {{ $t('fields.email_e_email') }}
                                </span>
                            </div>
                        </label>
                    </div>
                    <div class="row form__row" v-if="!isSystem">
                        <select class="form__field" v-model="formData.role">
                            <option
                                v-for="(option, index) in roleOptions"
                                :key="index"
                                :label="option.label"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div class="row form__row" v-if="isSystem">
                        <label class="form__label">
                            <input
                                ref="id"
                                type="text"
                                class="form__field"
                                name="id"
                                v-model.trim="$v.formData.id.$model"
                                :placeholder="$t('fields.system_id')"
                                required
                                autofocus
                                @focus="hasFocus = true"
                                @blur="hasFocus = false"
                                :class="{
                                    'is-invalid': $v.formData.id.$invalid,
                                    'is-dirty': $v.formData.id.$dirty,
                                }"
                            />

                            <div class="form__tooltip" v-if="hasFocus && $v.formData.id.$anyError">
                                <span v-if="!$v.formData.id.required">
                                    {{ $t('fields.id_e_required') }}
                                </span>
                            </div>
                        </label>
                    </div>
                </fieldset>
            </div>

            <dialog-buttons
                :submit-text="isSystem ? $t('buttons.add_system') : $t('buttons.send_invitation')"
                :disabled="loading"
            />
        </form>
    </div>
</template>

<script>
import * as api from '@/api';
import { required, email } from 'vuelidate/lib/validators';
import DialogHeader from '@/components/DialogHeader';
import DialogButtons from '@/components/DialogButtons';
import autofocus from '@/directives/autofocus';

export default {
    name: 'system-invite',
    components: {
        DialogHeader,
        DialogButtons,
    },
    props: {
        organizationId: {
            type: [String, Boolean],
            default: false,
        },
        invitationType: {
            type: String,
            default: 'person', // system-operator | system-service
        },
    },
    directives: {
        autofocus,
    },
    data() {
        return {
            formData: {
                email: '',
                role: 'person',
            },
            roleOptions: [
                {
                    value: 'owner',
                    label: this.$t('organizations.role_owner'),
                },
                {
                    value: 'admin',
                    label: this.$t('organizations.role_admin'),
                },
                {
                    value: 'technician',
                    label: this.$t('organizations.role_technician'),
                },
                {
                    value: 'person',
                    label: this.$t('organizations.role_member'),
                },
            ],
            hasFocus: false,
            loading: false,
        };
    },
    validations() {
        return {
            formData: {
                id: {
                    required,
                },
                email: {
                    required,
                    email,
                },
            },
        };
    },
    computed: {
        isSystem() {
            return this.invitationType.indexOf('system') >= 0;
        },
    },
    mounted() {
        this.prepareData();
    },
    methods: {
        submitHandler() {
            if (this.isSystem) {
                this.addSystem();
            } else {
                this.addPerson();
            }
        },
        prepareData() {
            if (this.isSystem) {
                this.formData = {
                    id: '',
                    type: this.invitationType.split('-')[1],
                };
            }
        },
        async addPerson() {
            this.loading = true;
            try {
                await api.organizations.addPerson(this.organizationId, this.formData);
                this.loading = false;
                this.$emit('close');
            } catch (error) {
                console.error(error);
                this.loading = false;
                this.$notify({
                    type: 'error',
                    text: this.$t('organizations.add_person_error'),
                });
            }
        },
        async addSystem() {
            this.loading = true;
            try {
                await api.organizations.addSystem(this.organizationId, this.formData);
                this.loading = false;
                this.$emit('close');
            } catch (error) {
                console.error(error);
                this.loading = false;
                this.$notify({
                    type: 'error',
                    text: this.$t('organizations.add_system_error'),
                });
            }
        },
    },
};
</script>
